import { useRef } from 'react';
import { StyleSheet } from 'react-native';
import { merge } from 'merge-anything';
import equal from 'fast-deep-equal/react';
import { useTheme, useBreakpoint } from '../hooks';
import { parseStyles, getStyleSystemOptions } from '../system';
import {
  flatten,
  flattenStyles,
  murmurhash,
  fromReactNativeStyles,
} from './utils';
import { isEmpty, get, isNull, isObject } from '../utils';

const createStyleSheet = (styleObject) => {
  return StyleSheet.create(styleObject);
};

const generated = { };
const created = { };

function generateStyles(styles) {
  const styleObject = flattenStyles(styles);
  const styleString = JSON.stringify(styleObject);

  const hash = murmurhash(styleString);
  if (!generated[hash]) {
    const styleSheet = createStyleSheet({ generated: styleObject });
    generated[hash] = styleSheet.generated;
    created[styleSheet.generated] = styleObject;
  }

  return generated[hash];
}

function getStyle(style) {
  let resolved = {};
  if (!isNull(style)) {
    if (Array.isArray(style)) {
      for (let i = 0; i < style.length; i += 1) {
        resolved = { ...resolved, ...getStyle(style[i]) };
      }
    } else if (typeof style === 'object') {
      resolved = style;
    } else if (created[style]) {
      resolved = created[style];
    }
  }
  return fromReactNativeStyles(resolved);
}

function getStyles(styles) {
  let resolved = { };
  for (const key in styles) {
    resolved[key] = getStyle(styles[key]);
  }
  return resolved;
}

function stylesAreResolved(style) {
  return isObject(style) && style._resolved;
}

function getStylesToParse(stylesDef, overrideStyles, props) {
  let { styles } = props;

  let stylesToParse = [stylesDef, overrideStyles];
  let stylesPassed = null;
  if (styles) {
    if (Array.isArray(styles)) {
      for (const style of styles) {
        if (stylesAreResolved(style)) {
          const { props: styleProps, ...namedStyles } = style;
          if (!stylesPassed) {
            stylesPassed = { ...namedStyles, props: { ...styleProps } };
          } else {
            for (const name in namedStyles) {
              const namedStyle = namedStyles[name];
              if (stylesPassed[name]) {
                if (Array.isArray(stylesPassed[name])) {
                  stylesPassed[name] = [...stylesPassed[name], namedStyle];
                } else {
                  stylesPassed[name] = [stylesPassed[name], namedStyle];
                }
              } else {
                stylesPassed[name] = namedStyle;
              }
            }
            if (styleProps) {
              stylesPassed.props = merge(stylesPassed.props, styleProps);
            }
          }
        } else {
          stylesToParse.push(style);
        }
      }
    } else if (!stylesAreResolved(styles)) {
      stylesToParse.push(styles);
    } else {
      stylesPassed = styles;
    }
  }
  return { stylesToParse, stylesPassed };
}

function createStyles(stylesDef, options = { }) {
  const {
    name,
    postApply,
    asProp = {},
    forward = false,
    ...systemOptions
  } = options;

  const className = typeof name === 'string' ? `${name.charAt(0).toLowerCase()}${name.slice(1)}` : name;

  if (forward) {
    const useStyles = (props) => {
      const theme = useTheme();
      let { props: overrideProps, ...overrideStyles } = theme.overrides[name] || {};
      if (props.styles) {
        if (Array.isArray(props.styles)) {
          return [stylesDef, overrideStyles, ...props.styles];
        }
        return [stylesDef, overrideStyles, props.styles];
      }
      return [stylesDef, overrideStyles];
    };
    return useStyles;
  }

  const styleSystemOptions = getStyleSystemOptions(systemOptions);

  const useStyles = (props) => {
    const lastBreakpoint = useRef();
    const lastProps = useRef();
    const lastStyles = useRef();

    const theme = useTheme();
    const [breakpoint, breakpoints] = useBreakpoint();
    // TODO: is checking for useLast a performance drop?
    let useLast = false;
    if (lastStyles.current) {
      useLast = (
        lastProps.current
        && equal(lastProps.current, props)
        && lastBreakpoint.current
        && equal(lastBreakpoint.current, breakpoint)
      );
    }
    lastProps.current = props;
    lastBreakpoint.current = breakpoint;

    if (useLast) {
      return lastStyles.current;
    }

    const override = theme.overrides[name] || {};
    let { props: overrideProps, ...overrideStyles } = override;
    // TODO: ^^ in order for theme override props to work. all components need to NOT use defaultProps. Thost are handled via props destrucuring in function body
    const system = props.styleSystemOptions
      ? getStyleSystemOptions(props.styleSystemOptions, styleSystemOptions)
      : styleSystemOptions;

    const propsWithSystemProps = {
      ...overrideProps,
      breakpoint,
      breakpoints,
      theme,
      styleSystemOptions: system,
      ...props,
      getStyle,
    };

    let styles = { props: { } };

    const { stylesToParse, stylesPassed } = getStylesToParse(
      stylesDef,
      overrideStyles,
      propsWithSystemProps
    );
    // let { styles: stylesPassed } = props;
    // const stylesPassedAreResolved =
    //   stylesPassed && isObject(stylesPassed) && stylesPassed._resolved;
    //
    // const stylesToParse = stylesPassedAreResolved
    //   ? [stylesDef, overrideStyles]
    //   : [stylesDef, overrideStyles, stylesPassed];
    const flattened = flatten(stylesToParse, propsWithSystemProps);
    const actualDefs = {};
    const rootVariants = {};
    let hasVariants = false;
    for (const key in flattened) {
      if (typeof flattened[key] === 'function' && flattened[key].isVariantResolver) {
        rootVariants[key] = flattened[key];
        hasVariants = true;
      } else {
        actualDefs[key] = flattened[key];
      }
    }
    if (!actualDefs.root) {
      actualDefs.root = {};
    }
    for (const key in actualDefs) {
      let resolved = parseStyles(actualDefs[key], propsWithSystemProps);
      if (key === 'root' && hasVariants) {
        const resolvedVariants = parseStyles(rootVariants, propsWithSystemProps);
        resolved = merge(resolved, resolvedVariants);
      }
      let { props: addProps, animations, ...resolvedStyles } = resolved;
      const stylesAsProps = { };
      for (const styleKey in asProp) {
        const prop = get(system.aliases, styleKey, styleKey);
        if (asProp[styleKey] !== false && !isNull(resolvedStyles[prop])) {
          stylesAsProps[styleKey] = resolvedStyles[styleKey];
          delete resolvedStyles[styleKey];
        }
      }

      if (!isEmpty(resolved)) {
        const generatedStyle = generateStyles(resolvedStyles);
        styles[key] = generatedStyle;
      } else {
        styles[key] = null;
      }

      if (addProps) {
        styles.props[key] = typeof addProps === 'function'
          ? { ...addProps(propsWithSystemProps, resolved), ...stylesAsProps }
          : { ...addProps, ...stylesAsProps };
      } else {
        styles.props[key] = { ...stylesAsProps };
      }
      if (animations) {
        styles.props[key].animations = animations;
      }
    }

    if (stylesPassed) {
      for (const key in stylesPassed) {
        if (key === 'root' || key === 'props') {
          continue;
        }
        if (name && (key === name || key === className)) {
          styles.root = [styles.root, stylesPassed[key]];
          styles.props.root = merge(styles.props.root, stylesPassed.props[key]);
        } else if (styles[key]) {
          styles[key] = [styles[key], stylesPassed[key]];
          styles.props[key] = merge(styles.props[key], stylesPassed.props[key]);
        } else {
          styles[key] = stylesPassed[key]
          styles.props[key] = stylesPassed.props[key];
        }
      }
    }
    if (props.animations) {
      if (!styles.props.root) styles.props.root = { animations: { } };
      if (typeof props.animations === 'function') {
        styles.props.root.animations = merge(
          styles.props.root.animations || { },
          props.animations(propsWithSystemProps, styles.root)
        )
      } else {
        styles.props.root.animations = merge(styles.props.root.animations || { }, props.animations);
      }
    }

    if (options.debug) console.log('DEBUG', options.name, styles);

    if (postApply) {
      let createdStyles = getStyles(styles);
      let postStyles = { };
      let postProps = { };
      if (Array.isArray(postApply)) {
        for (let i = 0; i < postApply.length; i += 1) {
          const post = postApply[i]({
            ...propsWithSystemProps,
            styles: createdStyles,
          });
          const { props: aProps, ...aStyles } = post;
          if (aStyles) {
            for (const key in aStyles) {
              aStyles[key] = parseStyles(aStyles[key], propsWithSystemProps);
            }
            postStyles = merge(postStyles, aStyles);
          }
          if (aProps) {
            postProps = merge(postProps, aProps);
          }
          createdStyles = merge(createdStyles, {
            props: postProps,
            ...postStyles,
          });
        }
      } else {
        const post = postApply({
          ...propsWithSystemProps,
          styles: createdStyles,
        });
        const { props: aProps, ...aStyles } = post;
        if (aStyles) {
          for (const key in aStyles) {
            aStyles[key] = parseStyles(aStyles[key], propsWithSystemProps);
          }
          postStyles = aStyles;
        }
        if (aProps) {
          postProps = aProps;
        }
      }
      for (const key in postStyles) {
        if (!isEmpty(postStyles[key])) {
          const generatedStyle = generateStyles(postStyles[key]);
          styles[key] = [styles[key], generatedStyle];
        }
      }
      styles.props = merge(styles.props, postProps);
    }
    styles._resolved = true;
    lastStyles.current = styles;
    return styles;
  };

  return useStyles;
}

export { createStyles, getStyle, getStyles };
